/**
 * @file Components/Sections/FeaturedHotels.jsx
 * @description Featured hotels section for the Home page.
 * Fetches real hotel data from Amadeus via /api/hotels/popular.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Hotel, MapPin, ArrowRight, Star, Building2, Loader2 } from 'lucide-react';
import { getPopularHotels } from '../../services/api';
import AnimatedButton from '../Ui/AnimatedButton';

const HOTEL_IMAGES = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1590073242678-70ee3fc28e8e?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1455587734955-081b22074882?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1549294413-26f195200c16?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1573052905904-34ad3d3b60a1?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1596178065887-1198b6148b2b?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1559599238-308793637427?w=600&h=400&fit=crop&q=80',
  'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&h=400&fit=crop&q=80',
];

function hashStr(s = '') {
  return s.split('').reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
}
function getHotelImg(hotel, index = 0) {
  if (hotel.media?.[0]?.uri) return hotel.media[0].uri;
  const h = Math.abs(hashStr(hotel.hotelId || hotel.name || '') + index);
  return HOTEL_IMAGES[h % HOTEL_IMAGES.length];
}


const FeaturedHotels = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['popularHotels'],
    queryFn: async () => {
      const res = await getPopularHotels(3);
      return res?.data?.data?.cities || [];
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const cities = data || [];

  if (isLoading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-[#243185]/40" />
        </div>
      </section>
    );
  }

  if (cities.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-sm font-bold text-[#243185] uppercase tracking-wider">Real-Time from Amadeus</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-1">
              Hotels Across Top Cities
            </h2>
            <p className="text-gray-500 mt-2 max-w-lg">
              Browse real hotels from our global distribution network — live data, real properties.
            </p>
          </div>
          <Link to="/hotels" className="hidden md:block">
            <AnimatedButton variant="outline" size="sm" icon={ArrowRight} iconPosition="right">
              Search All Hotels
            </AnimatedButton>
          </Link>
        </div>

        {/* City Hotel Grids */}
        <div className="space-y-10">
          {cities.slice(0, 4).map((cityData, ci) => (
            <motion.div
              key={cityData.city.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: ci * 0.1 }}
            >
              {/* City Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#243185]/10 flex items-center justify-center">
                  <MapPin size={16} className="text-[#243185]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{cityData.city.name}</h3>
                  <p className="text-xs text-gray-400">{cityData.city.country} &middot; {cityData.hotels.length} properties</p>
                </div>
                <Link to={`/hotels`} className="ml-auto text-xs text-[#243185] font-bold hover:underline flex items-center gap-1">
                  View all <ArrowRight size={12} />
                </Link>
              </div>

              {/* Hotel Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cityData.hotels.slice(0, 3).map((hotel, hi) => (
                  <Link
                    key={hotel.hotelId}
                    to="/hotels"
                    className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-[#243185]/20 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative h-36 overflow-hidden bg-gray-100">
                      <img src={getHotelImg(hotel, hi)} alt={hotel.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-bold text-gray-900 truncate group-hover:text-[#243185] transition-colors">{hotel.name}</h4>
                      <p className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-1">
                        <MapPin size={10} className="text-[#243185] flex-shrink-0" />
                        {hotel.address?.lines?.[0] || hotel.address?.cityName || cityData.city.name}
                      </p>
                      {hotel.distance && (
                        <p className="text-[10px] text-[#243185] font-medium mt-1">
                          {hotel.distance.value} {hotel.distance.unit} from center
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile CTA */}
        <div className="mt-8 text-center md:hidden">
          <Link to="/hotels">
            <AnimatedButton variant="gradient" size="md" icon={ArrowRight} iconPosition="right">
              Search All Hotels
            </AnimatedButton>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedHotels;
